import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { MonopolyView } from "../../../shared/games/monopoly";
import type { MonoStage, SfxName, StagePlayer } from "../../monopoly3d/stage";
import type { Palette } from "../../monopoly3d/textures";

export interface Insets { left: number; right: number; top: number; bottom: number }
export interface StageHandlersProp {
  onHover?(tile: number | null): void;
  onPick?(tile: number): void;
  onTokenPick?(seat: number): void;
  onDiceClick?(): void;
  /** true as soon as something is to be played (also right after a sync, before the first frame) */
  onBusy?(busy: boolean): void;
  sfx?(name: SfxName): void;
  onWheelHint?(): void;
}
export interface Stage3DProps {
  b: MonopolyView;
  over: boolean;
  winner: unknown;
  players: StagePlayer[];
  palette?: Palette;
  rollInvite: boolean;
  pickTiles: number[] | null;
  pickColor?: string;
  selected: number | null;
  focusTile: number | null;
  active: number | null;
  insets: Insets;
  handlers: StageHandlersProp;
  onReady(stage: MonoStage | null): void;
  /** WebGL is missing, the 3D module could not be loaded or the graphics context was lost */
  onFail(why: "start" | "load" | "lost"): void;
}

/**
 * The 3D board. The stage module (and with it Three.js) is loaded on demand; until then – and for good if WebGL does not work – the parent shows
 * the flat board. Everything the stage needs to know is pushed in from here; the stage plays the changes and tells us when it is busy.
 */
export function Stage3D(props: Stage3DProps) {
  const host = useRef<HTMLDivElement | null>(null);
  const P = useRef(props);
  P.current = props;
  const [stage, setStage] = useState<MonoStage | null>(null);

  useEffect(() => {
    let dead = false;
    let st: MonoStage | null = null;
    const el = host.current;
    if (!el) return;
    import("../../monopoly3d/stage").then(({ MonoStage }) => {
      if (dead) return;
      const h = () => P.current.handlers;
      st = MonoStage.create(el, {
        palette: P.current.palette,
        players: P.current.players,
        handlers: {
          onHover: (t) => h().onHover?.(t),
          onPick: (t) => h().onPick?.(t),
          onTokenPick: (s) => h().onTokenPick?.(s),
          onDiceClick: () => h().onDiceClick?.(),
          onBusy: (v) => h().onBusy?.(v),
          onWheelHint: () => h().onWheelHint?.(),
          sfx: (n) => h().sfx?.(n),
          onLost: () => P.current.onFail("lost"),
        },
      });
      if (!st) { P.current.onFail("start"); return; }
      const p = P.current;
      st.setInsets(p.insets);
      st.sync(p.b, p.over, p.winner);
      st.setActive(p.active);
      setStage(st);
      p.onReady(st);
    }).catch(() => { if (!dead) P.current.onFail("load"); });
    return () => {
      dead = true;
      if (st) { try { st.dispose(); } catch { /* ignore */ } }
      setStage(null);
      P.current.onReady(null);
    };
  }, []);

  /* the game state – before paint, so that the UI never shows a result that the board has not played yet */
  useLayoutEffect(() => {
    if (!stage) return;
    stage.sync(props.b, props.over, props.winner);
    props.handlers.onBusy?.(stage.hasWork);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, props.b, props.over, props.winner]);

  const playersKey = JSON.stringify(props.players);
  useEffect(() => { if (stage) stage.setPlayers(props.players); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [stage, playersKey]);
  const palKey = props.palette ? `${props.palette.light}${props.palette.dark}${props.palette.accent}` : "";
  useEffect(() => { if (stage && props.palette) stage.setPalette(props.palette); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [stage, palKey]);
  useEffect(() => { stage?.setRollInvite(props.rollInvite); }, [stage, props.rollInvite]);
  const pickKey = (props.pickTiles ?? []).join(",");
  useEffect(() => { stage?.setPickTiles(props.pickTiles, props.pickColor); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [stage, pickKey, props.pickColor]);
  useEffect(() => { stage?.setSelected(props.selected); }, [stage, props.selected]);
  useEffect(() => { stage?.setFocusTile(props.focusTile); }, [stage, props.focusTile]);
  useEffect(() => { stage?.setActive(props.active); }, [stage, props.active]);
  const { left, right, top, bottom } = props.insets;
  useEffect(() => { stage?.setInsets({ left, right, top, bottom }); }, [stage, left, right, top, bottom]);

  return <div ref={host} className="m3-canvas" data-ready={stage ? "1" : "0"} />;
}
